import { useState, useEffect, useCallback, useRef, type CSSProperties } from 'react';
import { api, ApiError } from '../api/client';
import './LicenseAssignment.css';
import './InventoryPull.css';

/* ─── Types ──────────────────────────────────────────────── */

type RunStatus = 'queued' | 'running' | 'completed' | 'failed';
type ItemStatus = 'pending' | 'ok' | 'error';
export type InventoryKind = 'serken' | 'regular';

interface InventoryRun {
  id: string;
  kind: InventoryKind;
  status: RunStatus;
  triggered_by: string | null;
  panels_total: number;
  panels_done: number;
  panels_errored: number;
  total_capacity: number | null;
  total_active: number | null;
  total_suspended: number | null;
  total_users: number | null;
  total_free: number | null;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

interface InventoryRunItem {
  run_id: string;
  inventory_panel_id: string;
  panel_reseller_id: string | null;
  panel_email: string;
  capacity: number;
  status: ItemStatus;
  active_users: number | null;
  suspended_users: number | null;
  total_users: number | null;
  free: number | null;
  error_code: string | null;
  error_message: string | null;
  polled_at: string | null;
}

interface RunDetail {
  run: InventoryRun;
  items: InventoryRunItem[];
}

interface Toast {
  type: 'success' | 'error';
  message: string;
}

/* ─── Helpers ────────────────────────────────────────────── */

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(iso).toLocaleDateString();
}

function durationLabel(start: string | null, end: string | null): string {
  if (!start) return '—';
  const endMs = end ? new Date(end).getTime() : Date.now();
  const ms = endMs - new Date(start).getTime();
  if (ms < 1000) return '<1s';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const restSec = sec % 60;
  return restSec === 0 ? `${min}m` : `${min}m ${restSec}s`;
}

const ChevronIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const PlayIcon = (): JSX.Element => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);
const RefreshIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 4v6h-6M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const CopyIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const DownloadIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const BarsIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="6" y1="20" x2="6" y2="12" />
    <line x1="12" y1="20" x2="12" y2="6" />
    <line x1="18" y1="20" x2="18" y2="14" />
  </svg>
);
const CloseIcon = (): JSX.Element => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/**
 * Visible ID for a panel row in the inventory output:
 *   serken  → the existing panel_reseller_id (e.g. "S4012", "S4191") — these
 *             are stable, meaningful identifiers already in the panels DB.
 *   regular → sequential row number "1", "2", … — regulars don't have
 *             meaningful reseller_ids (often just imported as 1..N), and the
 *             email-asc sort scrambles them, so 1..N in CSV order is cleaner.
 */
function panelIdLabel(it: InventoryRunItem, idx: number, kind: InventoryKind): string {
  if (kind === 'serken') {
    return it.panel_reseller_id ?? `#${idx + 1}`;
  }
  return String(idx + 1);
}

/**
 * Dashboard-only ordering.
 *
 * The API returns items sorted by panel_email (matches CSV / clipboard).
 * For the dashboard, re-sort so the operator's eye lands on trouble first:
 *
 *   1. Negative FREE (over-capacity) — most negative on top, red-flagged.
 *   2. FREE = 0 (full panels).
 *   3. Positive FREE ascending — almost-full first, then big-free.
 *   4. Errored panels last (FREE is null) — they need Retry, not action.
 *
 * IDs come from panels.reseller_id, which is the same string the operator
 * sees on the Admin Panels page — so dashboard, CSV, and clipboard all
 * agree on what "S4012" refers to.
 */
function sortForDashboard(
  items: InventoryRunItem[],
  kind: InventoryKind,
): (InventoryRunItem & { idLabel: string })[] {
  const labelled = items.map((it, idx) => ({ ...it, idLabel: panelIdLabel(it, idx, kind) }));
  return labelled.sort((a, b) => {
    const aErr = a.free === null;
    const bErr = b.free === null;
    if (aErr && bErr) return a.panel_email.localeCompare(b.panel_email);
    if (aErr) return 1;
    if (bErr) return -1;
    return (a.free as number) - (b.free as number);
  });
}

/**
 * Bucket count of panels by "free" license count, 1..MAX_FREE_BUCKET.
 * Errored panels (free=null) and free<1 / free>MAX_FREE_BUCKET are excluded —
 * the goal is a quick "do I have any near-full panels?" read at a glance.
 */
const MAX_FREE_BUCKET = 19;

function freeDistribution(items: InventoryRunItem[]): number[] {
  const buckets = new Array<number>(MAX_FREE_BUCKET).fill(0);
  for (const it of items) {
    if (it.status !== 'ok') continue;
    const f = it.free;
    if (typeof f !== 'number') continue;
    if (f < 1 || f > MAX_FREE_BUCKET) continue;
    buckets[f - 1] += 1;
  }
  return buckets;
}

/** TSV row order (mirrors the server-side CSV sort in src/routes/inventory-runs.ts):
 *    1. FREE > 0, ascending — low-FREE first so operators see "needs attention" rows on top
 *    2. FREE = 0 (full)
 *    3. FREE < 0 (overcap), ascending
 *    4. FREE = null (errored) last
 *  Tiebreak: panel_email asc. */
function sortForTsv(items: InventoryRunItem[]): InventoryRunItem[] {
  return [...items].sort((a, b) => {
    const af = a.free;
    const bf = b.free;
    const aErr = af === null;
    const bErr = bf === null;
    if (aErr && bErr) return a.panel_email.localeCompare(b.panel_email);
    if (aErr) return 1;
    if (bErr) return -1;
    const aPos = (af as number) > 0;
    const bPos = (bf as number) > 0;
    if (aPos && !bPos) return -1;
    if (!aPos && bPos) return 1;
    if (af !== bf) return (af as number) - (bf as number);
    return a.panel_email.localeCompare(b.panel_email);
  });
}

/** Build TSV (tab-separated) for clipboard — pastes cleanly into Sheets. */
function buildTsv(items: InventoryRunItem[], totalFree: number | null, kind: InventoryKind): string {
  const lines: string[] = [];
  const tf =
    totalFree ??
    items.reduce((acc, it) => (it.status === 'ok' && typeof it.free === 'number' ? acc + it.free : acc), 0);
  // Row 1: 4 blank, "TOTAL FREE:", number, 2 blank → 8 cells
  lines.push(['', '', '', '', 'TOTAL FREE:', String(tf), '', ''].join('\t'));
  // Row 2: headers
  lines.push(['ID', 'LOGIN', 'ACTIVE_USERS', 'SUSPENDED_USERS', 'TOTAL_USERS', 'FREE', 'DELEGATION', 'ERROR'].join('\t'));
  const sortedItems = sortForTsv(items);
  sortedItems.forEach((it, idx) => {
    const errorText = it.error_code
      ? it.error_message
        ? `${it.error_code}: ${it.error_message}`
        : it.error_code
      : '';
    lines.push(
      [
        panelIdLabel(it, idx, kind),
        it.panel_email,
        it.active_users ?? '',
        it.suspended_users ?? '',
        it.total_users ?? '',
        it.free ?? '',
        '', // DELEGATION blank
        errorText,
      ]
        .map((c) => String(c))
        .join('\t'),
    );
  });
  return lines.join('\n');
}

/* ─── Main component ─────────────────────────────────────── */

export function InventoryPull(): JSX.Element {
  const [kind, setKind] = useState<InventoryKind>('serken');
  const [runs, setRuns] = useState<InventoryRun[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [starting, setStarting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  // Switching kinds tears down list + detail polling so we don't render stale
  // data from the previous bucket while new fetches are in flight.
  const switchKind = (k: InventoryKind): void => {
    if (k === kind) return;
    setKind(k);
    setRuns(null);
    setSelectedId(null);
    setDetail(null);
  };

  /* ─── List view fetch + polling ──────────────────────── */

  const refreshList = useCallback(async (): Promise<void> => {
    try {
      const data = await api<InventoryRun[]>(`/api/inventory-runs?kind=${kind}`);
      setRuns(data);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load runs');
    }
  }, [showToast, kind]);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  const listPollRef = useRef<number | null>(null);
  useEffect(() => {
    if (selectedId !== null) return;
    const hasInflight = runs?.some((r) => r.status === 'queued' || r.status === 'running');
    const intervalMs = hasInflight ? 3000 : 7000;
    if (listPollRef.current !== null) {
      window.clearInterval(listPollRef.current);
    }
    listPollRef.current = window.setInterval(() => {
      void refreshList();
    }, intervalMs);
    return () => {
      if (listPollRef.current !== null) {
        window.clearInterval(listPollRef.current);
        listPollRef.current = null;
      }
    };
  }, [runs, selectedId, refreshList]);

  /* ─── Detail view fetch + polling ────────────────────── */

  const refreshDetail = useCallback(async (id: string): Promise<void> => {
    try {
      const data = await api<RunDetail>(`/api/inventory-runs/${id}`);
      setDetail(data);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load run');
    }
  }, [showToast]);

  const detailPollRef = useRef<number | null>(null);
  useEffect(() => {
    if (selectedId === null) {
      setDetail(null);
      if (detailPollRef.current !== null) {
        window.clearInterval(detailPollRef.current);
        detailPollRef.current = null;
      }
      return;
    }
    void refreshDetail(selectedId);
    detailPollRef.current = window.setInterval(() => {
      void refreshDetail(selectedId);
    }, 2500);
    return () => {
      if (detailPollRef.current !== null) {
        window.clearInterval(detailPollRef.current);
        detailPollRef.current = null;
      }
    };
  }, [selectedId, refreshDetail]);

  useEffect(() => {
    if (!detail) return;
    if (detail.run.status === 'completed' || detail.run.status === 'failed') {
      if (detailPollRef.current !== null) {
        window.clearInterval(detailPollRef.current);
        detailPollRef.current = null;
      }
    } else if (selectedId !== null && detailPollRef.current === null) {
      // Re-arm polling if the run flipped back to running (retry-errors)
      detailPollRef.current = window.setInterval(() => {
        void refreshDetail(selectedId);
      }, 2500);
    }
  }, [detail, selectedId, refreshDetail]);

  /* ─── Start a full pull ──────────────────────────────── */

  const startRun = async (): Promise<void> => {
    setStarting(true);
    try {
      const result = await api<{ runId: string; panelsQueued: number }>('/api/inventory-runs', {
        method: 'POST',
        body: JSON.stringify({ kind }),
      });
      showToast('success', `Started — ${result.panelsQueued} panels queued`);
      await refreshList();
      setSelectedId(result.runId);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RUN_IN_PROGRESS') {
        const existingRunId = (err.data as { existingRunId?: string } | undefined)?.existingRunId;
        if (existingRunId) {
          showToast('success', 'A pull is already in progress — opening it now.');
          await refreshList();
          setSelectedId(existingRunId);
          return;
        }
      }
      const msg =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Failed to start pull';
      showToast('error', msg);
    } finally {
      setStarting(false);
    }
  };

  /* ─── Retry errored panels of the open run ───────────── */

  const retryErrors = async (id: string): Promise<void> => {
    setRetrying(true);
    try {
      const result = await api<{ runId: string; retriedPanels: number }>(
        `/api/inventory-runs/${id}/retry-errors`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      showToast('success', `Retrying ${result.retriedPanels} failed panel${result.retriedPanels === 1 ? '' : 's'}…`);
      await refreshDetail(id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Retry failed';
      showToast('error', msg);
    } finally {
      setRetrying(false);
    }
  };

  /* ─── CSV / clipboard ────────────────────────────────── */

  const copyCsv = async (): Promise<void> => {
    if (!detail) return;
    const tsv = buildTsv(detail.items, detail.run.total_free, detail.run.kind);
    try {
      await navigator.clipboard.writeText(tsv);
      showToast('success', 'Copied — paste into Google Sheets.');
    } catch {
      showToast('error', 'Clipboard write blocked. Try Download CSV instead.');
    }
  };

  const downloadCsv = (): void => {
    if (!detail) return;
    window.location.href = `/api/inventory-runs/${detail.run.id}/csv`;
  };

  /* ─── Render: detail view ────────────────────────────── */

  if (selectedId !== null) {
    return (
      <div className="lic-root">
        <div className="lic-scroll">
          <header className="lic-header">
            <button type="button" className="lic-back" onClick={() => setSelectedId(null)}>
              <ChevronIcon />
              <span>Back to pulls</span>
            </button>
          </header>
          {detail ? (
            <DetailView
              detail={detail}
              onRefresh={() => void refreshDetail(detail.run.id)}
              onRetryErrors={() => void retryErrors(detail.run.id)}
              onCopyCsv={() => void copyCsv()}
              onDownloadCsv={downloadCsv}
              retrying={retrying}
            />
          ) : (
            <div className="lic-loading">Loading pull…</div>
          )}
        </div>
        {toast && <div className={`lic-toast lic-toast--${toast.type}`}>{toast.message}</div>}
      </div>
    );
  }

  /* ─── Render: list view ──────────────────────────────── */

  const totalRuns = runs?.length ?? 0;
  const inflight = runs?.find((r) => r.status === 'queued' || r.status === 'running') ?? null;

  return (
    <div className="lic-root">
      <div className="lic-scroll">
        <header className="lic-header">
          <div className="lic-header-row">
            <div>
              <h1 className="lic-title">Inventory Pull</h1>
              <p className="lic-subtitle">
                Snapshot active and suspended user counts on every panel. Output matches the team's
                Google Sheets format — copy or download to paste into the daily pull sheet.
              </p>
              <div className="inv-kind-tabs">
                <button
                  type="button"
                  className={`inv-kind-tab ${kind === 'serken' ? 'inv-kind-tab--active' : ''}`}
                  onClick={() => switchKind('serken')}
                >
                  Serken
                </button>
                <button
                  type="button"
                  className={`inv-kind-tab ${kind === 'regular' ? 'inv-kind-tab--active' : ''}`}
                  onClick={() => switchKind('regular')}
                >
                  Regular
                </button>
              </div>
            </div>
            <button
              type="button"
              className="lic-btn lic-btn--primary"
              onClick={startRun}
              disabled={starting || inflight !== null}
              title={inflight ? 'A pull is already in progress' : `Pull inventory across all ${kind} panels`}
            >
              <PlayIcon />
              {starting ? 'Starting…' : inflight ? 'Pull in progress…' : 'Pull Inventory'}
            </button>
          </div>
        </header>

        <section className="lic-card">
          <div className="lic-card-header">
            <h2 className="lic-card-title">Pulls ({totalRuns})</h2>
            <button type="button" className="lic-icon-btn" onClick={() => void refreshList()} title="Refresh">
              <RefreshIcon />
            </button>
          </div>

          {runs === null && <div className="lic-empty">Loading…</div>}

          {runs !== null && runs.length === 0 && (
            <div className="lic-empty">
              <h3 className="lic-empty-title">No pulls yet</h3>
              <p className="lic-empty-text">
                Click <strong>Pull Inventory</strong> above to take the first snapshot of every panel.
              </p>
            </div>
          )}

          {runs !== null && runs.length > 0 && (
            <table className="lic-table">
              <thead>
                <tr>
                  <th>Pull</th>
                  <th>Status</th>
                  <th className="lic-col-num">Panels</th>
                  <th className="lic-col-num">Total Free</th>
                  <th className="lic-col-num">Errors</th>
                  <th>Triggered by</th>
                  <th className="lic-col-date">Started</th>
                  <th className="lic-col-date">Duration</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="lic-row" onClick={() => setSelectedId(r.id)}>
                    <td className="lic-cell-mono lic-cell-faint">{r.id.slice(0, 8)}</td>
                    <td>
                      <span className={`lic-badge lic-badge--${badgeFromStatus(r.status)}`}>{r.status}</span>
                    </td>
                    <td className="lic-col-num">
                      <span className="lic-num lic-num--ink">{r.panels_total}</span>
                    </td>
                    <td className="lic-col-num">
                      <span className="lic-num lic-num--sage">{r.total_free ?? '—'}</span>
                    </td>
                    <td className="lic-col-num">
                      <span className={`lic-num ${r.panels_errored > 0 ? 'lic-num--rose' : 'lic-num--muted'}`}>
                        {r.panels_errored}
                      </span>
                    </td>
                    <td className="lic-cell-mono lic-cell-faint">{r.triggered_by ?? '—'}</td>
                    <td className="lic-col-date lic-cell-faint">{relativeTime(r.started_at ?? r.created_at)}</td>
                    <td className="lic-col-date lic-cell-faint">{durationLabel(r.started_at, r.finished_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
      {toast && <div className={`lic-toast lic-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

/* The badge classes from LicenseAssignment.css use 'completed' not 'ok' — map status names. */
function badgeFromStatus(s: RunStatus): string {
  return s; // matches lic-badge--queued/running/completed/failed
}

/* ─── Detail view ────────────────────────────────────────── */

function DetailView({
  detail,
  onRefresh,
  onRetryErrors,
  onCopyCsv,
  onDownloadCsv,
  retrying,
}: {
  detail: RunDetail;
  onRefresh: () => void;
  onRetryErrors: () => void;
  onCopyCsv: () => void;
  onDownloadCsv: () => void;
  retrying: boolean;
}): JSX.Element {
  const { run, items } = detail;
  const inflight = run.status === 'queued' || run.status === 'running';
  const canRetry = run.status === 'completed' && run.panels_errored > 0;
  const [showDist, setShowDist] = useState(false);
  const distBuckets = freeDistribution(items);
  const distPanelCount = distBuckets.reduce((a, b) => a + b, 0);

  // Close modal on Escape — small ergonomic touch since the modal has no other
  // chrome and operators are keyboard-driven for the rest of this page.
  useEffect(() => {
    if (!showDist) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setShowDist(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showDist]);

  return (
    <>
      <section className="lic-summary-card">
        <div className="lic-summary-top">
          <div>
            <h2 className="lic-summary-title">
              Pull <span className="lic-cell-mono lic-cell-faint">{run.id.slice(0, 8)}</span>
            </h2>
            <div className="lic-summary-meta">
              Triggered {run.triggered_by ? <>by <strong>{run.triggered_by}</strong></> : null} ·{' '}
              {relativeTime(run.created_at)}
              {run.started_at && <> · ran {durationLabel(run.started_at, run.finished_at)}</>}
            </div>
          </div>
          <div className="lic-summary-actions">
            <span className={`lic-badge lic-badge--${badgeFromStatus(run.status)}`}>{run.status}</span>
            <button type="button" className="lic-icon-btn" onClick={onRefresh} title="Refresh">
              <RefreshIcon />
            </button>
          </div>
        </div>

        {run.error_message && <div className="lic-summary-error">{run.error_message}</div>}

        <div className="lic-summary-grid">
          <SummaryStat label="Panels" value={run.panels_total} hint="total" />
          <SummaryStat label="Polled OK" value={run.panels_done} variant="sage" />
          <SummaryStat
            label="Errored"
            value={run.panels_errored}
            variant={run.panels_errored > 0 ? 'rose' : 'muted'}
          />
          <SummaryStat label="Capacity" value={run.total_capacity ?? 0} variant="muted" hint="seats" />
          <SummaryStat label="Active" value={run.total_active ?? 0} hint="users" />
          <SummaryStat label="Suspended" value={run.total_suspended ?? 0} variant="honey" />
          <SummaryStat label="TOTAL FREE" value={run.total_free ?? 0} variant="sage" hint="seats free" />
        </div>

        <div className="inv-action-row">
          <button
            type="button"
            className="lic-btn lic-btn--secondary"
            onClick={() => setShowDist(true)}
            disabled={distPanelCount === 0}
            title="Histogram of panels grouped by free seats (1-19)"
          >
            <BarsIcon />
            <span>See Supply Distribution Breakdown</span>
          </button>
          <button
            type="button"
            className="lic-btn lic-btn--secondary"
            onClick={onCopyCsv}
            disabled={items.length === 0}
            title="Copy as TSV — pastes cleanly into Google Sheets"
          >
            <CopyIcon />
            <span>Copy CSV</span>
          </button>
          <button
            type="button"
            className="lic-btn lic-btn--secondary"
            onClick={onDownloadCsv}
            disabled={items.length === 0}
            title="Download as .csv file"
          >
            <DownloadIcon />
            <span>Download CSV</span>
          </button>
          {canRetry && (
            <button
              type="button"
              className="lic-btn lic-btn--primary"
              onClick={onRetryErrors}
              disabled={retrying}
              title="Re-poll only the panels that errored"
            >
              <RefreshIcon />
              <span>{retrying ? 'Retrying…' : `Retry ${run.panels_errored} failed`}</span>
            </button>
          )}
        </div>
      </section>

      <section className="lic-card">
        <div className="lic-card-header">
          <h2 className="lic-card-title">
            Panel snapshot ({items.length})
            {inflight && <span className="lic-inflight-pulse" />}
          </h2>
        </div>

        {items.length === 0 && (
          <div className="lic-empty">
            <span>No panels in this pull.</span>
          </div>
        )}

        {items.length > 0 && (
          <table className="lic-table inv-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>LOGIN</th>
                <th className="lic-col-num">ACTIVE</th>
                <th className="lic-col-num">SUSPENDED</th>
                <th className="lic-col-num">TOTAL</th>
                <th className="lic-col-num">FREE ↑</th>
                <th>ERROR</th>
              </tr>
            </thead>
            <tbody>
              {sortForDashboard(items, detail.run.kind).map((it) => {
                const overCap = it.free !== null && it.free < 0;
                return (
                  <tr
                    key={it.inventory_panel_id}
                    className={overCap ? 'inv-row--overcap' : ''}
                  >
                    <td className="lic-cell-mono">{it.idLabel}</td>
                    <td className="lic-cell-mono">{it.panel_email}</td>
                    <td className="lic-col-num">{it.active_users ?? '—'}</td>
                    <td className="lic-col-num lic-cell-faint">{it.suspended_users ?? '—'}</td>
                    <td className="lic-col-num">{it.total_users ?? '—'}</td>
                    <td className="lic-col-num">
                      {overCap ? (
                        <span className="inv-free-overcap">{it.free}</span>
                      ) : (
                        <span
                          className={`lic-num ${
                            it.free === null
                              ? 'lic-num--muted'
                              : it.free > 0
                                ? 'lic-num--sage'
                                : 'lic-num--muted'
                          }`}
                        >
                          {it.free ?? '—'}
                        </span>
                      )}
                    </td>
                    <td className="lic-cell-faint lic-cell-detail">
                      {it.error_code ? (
                        <span className="inv-error">
                          <strong>{it.error_code}</strong>
                          {it.error_message ? <> — {it.error_message}</> : null}
                        </span>
                      ) : (
                        <span className="lic-cell-faint">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {showDist && (
        <SupplyDistributionModal
          buckets={distBuckets}
          totalPanels={distPanelCount}
          onClose={() => setShowDist(false)}
        />
      )}
    </>
  );
}

function SupplyDistributionModal({
  buckets,
  totalPanels,
  onClose,
}: {
  buckets: number[];
  totalPanels: number;
  onClose: () => void;
}): JSX.Element {
  const maxCount = buckets.reduce((m, c) => (c > m ? c : m), 0);
  return (
    <div
      className="inv-dist-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="inv-dist-modal" role="dialog" aria-label="Supply distribution breakdown">
        <div className="inv-dist-modal-header">
          <div>
            <h3 className="inv-dist-modal-title">Supply Distribution</h3>
            <p className="inv-dist-modal-sub">
              Panels grouped by free seats — {totalPanels} panel{totalPanels === 1 ? '' : 's'} with 1-19 free
            </p>
          </div>
          <button type="button" className="lic-icon-btn" onClick={onClose} title="Close">
            <CloseIcon />
          </button>
        </div>
        <div className="inv-dist-modal-body">
          <div className="inv-dist-grid">
            {buckets.map((count, i) => {
              const free = i + 1;
              const empty = count === 0;
              const intensity = maxCount === 0 ? 0 : count / maxCount;
              return (
                <div
                  key={free}
                  className={`inv-dist-cell ${empty ? 'inv-dist-cell--empty' : ''}`}
                  style={empty ? undefined : ({ '--inv-dist-fill': intensity } as CSSProperties)}
                >
                  <span className="inv-dist-cell-key">{free} free</span>
                  <span className="inv-dist-cell-count">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryStat({
  label,
  value,
  variant = 'default',
  hint,
}: {
  label: string;
  value: number;
  variant?: 'default' | 'sage' | 'rose' | 'honey' | 'muted';
  hint?: string;
}): JSX.Element {
  return (
    <div className="lic-summary-stat">
      <span className="lic-summary-stat-label">{label}</span>
      <span className={`lic-summary-stat-value lic-summary-stat-value--${variant}`}>{value}</span>
      {hint && <span className="lic-summary-stat-hint">{hint}</span>}
    </div>
  );
}
